import {Component} from 'panel';

import {KEY_VALUES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {isValidEmailAddress} from 'common/util/string';
import type {User} from 'common/report/queries/app-api/legacy/org-types';
import type {Section} from 'common/components/items-menu/types';

import template from './index.jade';
import css from './index.styl?inline';

interface EmailRecipientsInputState {
  textInputIdx: number; // integer index for selected or new recipient
  inputValue: string;
  recipientInputEl: HTMLInputElement; // a direct reference to the recipient <input> element (passed to <mp-items-menu> for keyboard accessibility)
}

interface EmailRecipient extends User {
  invalidAddress?: boolean;
}

export class EmailRecipientsInput extends Component<EmailRecipientsInputState> {
  public projectMembersMap: Map<string, EmailRecipient> = null;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      borderless: `boolean`,
      disabled: `boolean`,
      recipients: {type: `json`, default: []},
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        textInputIdx: null, // integer index for selected or new recipient
        inputValue: ``,
        recipientInputEl: null, // a direct reference to the recipient <input> element (passed to <mp-items-menu> for keyboard accessibility)
      },
      helpers: {
        recipientFromInput: (input: string, sections: Section[]) =>
          sections.some((section) => section.items.length)
            ? null
            : [
                {
                  items: [
                    {
                      label: `Add "${input}"`,
                      icon: `plus`,
                      value: {id: null, email: input},
                    },
                  ],
                },
              ],
        /* --- Hooks --- */
        handleRecipientInputInsert: (vnode) =>
          requestAnimationFrame(() => {
            // we need to access <input> element directly to provide keyboard accessibility to <mp-items-menu>
            this.update({recipientInputEl: vnode.elm.shadowRoot.querySelector(`input`)});
          }),

        /* --- DOM event listeners --- */
        handleContainerClick: () => {
          // place text input after recipients
          this.update({textInputIdx: this.attr(`recipients`).length});
        },
        handleRecipientInputFocusOut: () => {
          this.addRecipientFromInput();
          this.update({textInputIdx: null});
        },
        handleRecipientInputKeydown: (ev) => {
          // this key handler is only triggered when the project members menu isn't open
          // when the project members menu is open, the <mp-items-menu> steals keydown events (via keydownProxyEl) and blocks this handler
          switch (ev.key) {
            case KEY_VALUES.BACKSPACE: {
              const {inputValue, textInputIdx} = this.state;
              if (!inputValue && textInputIdx > 0) {
                this.helpers.removeRecipientAt(textInputIdx - 1);
              }
              break;
            }
            case KEY_VALUES.TAB: {
              if (this.state.inputValue) {
                this.addRecipientFromInput();
                ev.preventDefault(); // prevents input from losing focus
              }
              break;
            }
            case KEY_VALUES.ENTER: {
              this.addRecipientFromInput();
              break;
            }
            case KEY_VALUES.ESCAPE: {
              this.addRecipientFromInput();
              this.update({textInputIdx: null});
              break;
            }
          }
        },
        clickedProjectMember: ({detail}) => {
          const {textInputIdx} = this.state;
          const recipient = this.validateRecipient(detail.item.value.email);
          if (textInputIdx === this.attr(`recipients`).length) {
            this.update({
              inputValue: ``,
              textInputIdx: textInputIdx + 1,
            });
            this.dispatchChangedEvent([...this.attr(`recipients`), recipient]);
          } else {
            const recipients = this.attr(`recipients`).slice();
            recipients[textInputIdx] = recipient;
            this.update({
              inputValue: ``,
              textInputIdx: recipients.length,
            });
            this.dispatchChangedEvent(recipients);
          }
        },

        /* --- actions --- */
        selectRecipientAt: (idx) => {
          this.update({
            inputValue: this.attr(`recipients`)[idx].email,
            textInputIdx: idx,
          });
        },
        removeRecipientAt: (idx) => {
          const recipients = this.attr(`recipients`).slice();
          recipients.splice(idx, 1);
          this.update({textInputIdx: recipients.length});
          this.dispatchChangedEvent(recipients);
        },
        getProjectMembersMenuSection: () => {
          const addedEmails = new Set(this.attr(`recipients`).map((r) => r.email));
          const items = [...this.projectMembersMap.values()]
            .filter((m) => !addedEmails.has(m.email))
            .map((m) => ({
              label: `${m.name} (${m.email})`,
              icon: `profile`,
              value: m,
            }));
          return {items};
        },
        checkRecipientIfSubsequentDuplicate: (recipient, idx) => {
          const firstIdx = this.attr(`recipients`).findIndex((r) => r.email === recipient.email);
          return firstIdx !== idx;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.projectMembersMap) {
      throw new Error(`"projectMembersMap" is a required component prop`);
    }
  }

  validateRecipient(email: string) {
    const recipient = this.projectMembersMap.has(email)
      ? {...this.projectMembersMap.get(email)}
      : {email, invalidAddress: false, id: null};

    // don't validate project members
    const invalidAddress = !recipient.id && !isValidEmailAddress(recipient.email);
    if (invalidAddress) {
      // only modify the recipient object if necessary
      recipient.invalidAddress = invalidAddress;
    }

    return recipient;
  }

  addRecipientFromInput() {
    const {inputValue, textInputIdx} = this.state;
    const value = inputValue.trim();
    if (value) {
      const recipient = this.validateRecipient(value);

      if (textInputIdx === this.attr(`recipients`).length) {
        this.update({
          inputValue: ``,
          textInputIdx: textInputIdx + 1,
        });
        this.dispatchChangedEvent([...this.attr(`recipients`), recipient]);
      } else {
        const recipients = this.attr(`recipients`).slice();
        recipients[textInputIdx] = recipient;
        this.update({
          inputValue: ``,
          textInputIdx: recipients.length,
        });
        this.dispatchChangedEvent(recipients);
      }
    }
  }

  checkAnyDuplicates(recipients) {
    const emails = new Set();
    for (const r of recipients) {
      if (emails.has(r.email)) {
        return true;
      }
      emails.add(r.email);
    }
    return false;
  }

  dispatchChangedEvent(recipients) {
    const valid = recipients.every((r) => !r.invalidAddress) && !this.checkAnyDuplicates(recipients);
    this.dispatchEvent(new CustomEvent(`changed`, {detail: {recipients, valid}}));
  }
}

export const EmailRecipientsInputEl = defineAndMakeJsxFactory(`mp-email-recipients-input`, EmailRecipientsInput);
